// =============================================================================
// FILE: packages/renderer/src/config/fris.content.ts
// PURPOSE: Static per-feature FRIS content — readiness level, what's working,
//   and what's coming next. Always available (no IPC dependency).
//   The FrisIndicator merges this with live FRIS probe data when available.
// =============================================================================

export type FrisLevel = 'green' | 'yellow' | 'red';

export interface FrisContent {
  /** Human label shown in popover header. */
  label: string;
  /** Overall readiness level (may be overridden by live FRIS probe). */
  level: FrisLevel;
  /** One-line summary of current status. */
  summary: string;
  /** What is fully working right now. */
  working: string[];
  /** What is in progress or planned next. */
  coming: string[];
  /** Optional: caveats / limitations. */
  notes?: string;
}

export const FRIS_CONTENT: Record<string, FrisContent> = {

  // ── Core modules ──────────────────────────────────────────────────────────

  dashboard: {
    label:   'Operations Dashboard',
    level:   'green',
    summary: 'Live operations overview — fully operational.',
    working: [
      'Real-time project & asset KPIs',
      'Connectivity mode indicator (Live / Secure Standalone)',
      'Parallax tile layout with warp-drive animation',
      'Clock toggle (12 / 24-hour)',
    ],
    coming: [
      'Configurable tile pinning per user',
      'Threshold-based alert badges on tiles',
    ],
  },

  projects: {
    label:   'Projects',
    level:   'green',
    summary: 'Full project lifecycle management — production ready.',
    working: [
      'Create, edit, archive projects',
      'Project folder hierarchy (PFS)',
      'Phase + status tracking',
      'Project-to-asset linking',
    ],
    coming: [
      'Gantt timeline view (Phase 2)',
      'Budget vs actuals integration',
    ],
  },

  assets: {
    label:   'Asset Registry',
    level:   'green',
    summary: 'Asset register and IFC import — fully operational.',
    working: [
      'CRUD for all asset types',
      'IFC import & geometry metadata',
      'Asset-to-rack / building mapping',
      'Barcode / QR asset ID generation',
    ],
    coming: [
      'CAD/IFC 3D viewer (Phase 2)',
      'Bulk CSV import wizard',
    ],
  },

  buildings: {
    label:   'Building Hierarchy',
    level:   'green',
    summary: 'Multi-site building management — production ready.',
    working: [
      'Site → Floor → Room hierarchy',
      'Revit linkage metadata',
      'GPS coordinates per site',
    ],
    coming: [
      'Interactive floor-plan viewer',
      'Heat-map overlays for cable density',
    ],
  },

  racks: {
    label:   'Rack & Telecom Spaces',
    level:   'green',
    summary: 'Rack elevation editor — production ready.',
    working: [
      'U-position assignment engine',
      'Port-level occupancy tracking',
      'Thermal zone classifications',
      'CSV / PDF rack schedule export',
    ],
    coming: [
      'Drag-and-drop elevation editor',
      'Power density calculations',
    ],
  },

  cabling: {
    label:   'Cabling Workspace',
    level:   'green',
    summary: 'Port-level connectivity graph — production ready.',
    working: [
      'End-to-end cable path tracing',
      'TIA-568 / ISO 11801 compliance checks',
      'Cable schedule report generation',
      'Patch panel association',
    ],
    coming: [
      'Automated slack + bend-radius warnings',
      'Fibre vs copper separation rules',
    ],
  },

  documents: {
    label:   'Documents & Drawings',
    level:   'green',
    summary: 'Document register with SharePoint binding — operational.',
    working: [
      'Version-controlled drawing register',
      'Revision tracking (A, B, C…)',
      'SharePoint / OneDrive binding',
      'PDF viewer integration',
    ],
    coming: [
      'Automated transmittal generation',
      'In-app markup & redline tools',
    ],
  },

  'doc-control': {
    label:   'Document Control Engine',
    level:   'yellow',
    summary: 'Authoritative document registry — MVP read-only mode.',
    working: [
      'Read-only document register view',
      'Status column (IFC, IFR, AFC…)',
      'Discipline + sheet type filters',
    ],
    coming: [
      'Full transmittal workflow',
      'RFI + submittal linking',
      'Approval matrix configuration',
    ],
  },

  field: {
    label:   'Field Operations',
    level:   'green',
    summary: 'Field visit workflow and site captures — operational.',
    working: [
      'Field visit creation & scheduling',
      'Photo capture with GPS tag',
      'Punch list item generation',
      'Offline-first data entry',
    ],
    coming: [
      'Auto-sync to SharePoint on reconnect',
      'Voice-to-text site notes',
    ],
  },

  qaqc: {
    label:   'QA / QC Gate',
    level:   'green',
    summary: 'Phase 1 validation engine — fully operational.',
    working: [
      'Pre-submission checklist engine',
      'Automated rule checks per discipline',
      'Pass / Fail / Warning status per item',
    ],
    coming: [
      'Custom rule builder (Phase 2)',
      'Integration with COBie exports',
    ],
  },

  propagation: {
    label:   'Change Propagation — GCPE',
    level:   'yellow',
    summary: 'Global Change Propagation Engine — beta.',
    working: [
      'Change event capture and queuing',
      'Downstream dependency mapping',
      'Manual approval before propagation',
    ],
    coming: [
      'Auto-propagation with conflict detection',
      'Rollback / undo propagation chain',
    ],
  },

  sync: {
    label:   'Synchronisation Engine',
    level:   'yellow',
    summary: 'Local + SharePoint sync — M365 OAuth pending.',
    working: [
      'Local SQLite sync queue operational',
      'Manual push / pull controls',
      'Conflict detection UI',
      'Folder monitor with live file events',
    ],
    coming: [
      'M365 OAuth token configuration',
      'Auto-resolve conflict policies',
    ],
  },

  schedule: {
    label:   'Calendar Engine',
    level:   'yellow',
    summary: 'Scheduling engine — local calendar operational, M365 sync beta.',
    working: [
      'Month / week / day calendar views',
      'Create, edit, delete events offline',
      'Event kinds: deadline, meeting, milestone',
      'iCal import support',
    ],
    coming: [
      'M365 OAuth bidirectional sync',
      'Project deadline auto-population',
      'Teams meeting generation',
    ],
  },

  sie: {
    label:   'Smart Ingestion Engine',
    level:   'green',
    summary: 'BIM file ingestion and metadata extraction — fully operational.',
    working: [
      'Drag-and-drop BIM file ingestion',
      'Metadata extraction (IFC, DWG, PDF)',
      'Auto-project linking from filename patterns',
      'SQLite document register write-back',
    ],
    coming: [
      'AI-assisted drawing classification',
      'Batch folder watch + auto-ingest',
    ],
  },

  comms: {
    label:   'Communications — B4',
    level:   'green',
    summary: 'Suggest-mode email follow-ups — production ready.',
    working: [
      'RFI & task overdue detection',
      'Draft email generation via AI',
      'Operator review before send',
      'Email kill-switch per tenant',
    ],
    coming: [
      'SMS / Teams channel follow-ups',
      'Customisable trigger templates',
    ],
  },

  ai: {
    label:   'Emmy — Engineering Advisor',
    level:   'yellow',
    summary: 'Tiered AI advisor — operational with model selection by tier.',
    working: [
      'Streaming SSE chat interface',
      'Tier-locked model routing (Gemini / Claude)',
      'BIM-context system prompt',
      'Chat history per session',
    ],
    coming: [
      'File attachment context (DWG/IFC)',
      'Project-aware memory across sessions',
    ],
  },

  'coach-validation': {
    label:   'Coach Validation',
    level:   'yellow',
    summary: 'P1-01 Project Coach — read-only summary, beta.',
    working: [
      'Project health snapshot',
      'Gap analysis against RCDD standards',
    ],
    coming: [
      'Interactive fix recommendations',
      'Export to PDF compliance report',
    ],
  },

  autopilot: {
    label:   'Project Autopilot — B5',
    level:   'yellow',
    summary: 'Plan-only autopilot — actions require explicit approval.',
    working: [
      'Project plan generation via AI',
      'Operator approval step before any action',
      'Read-only draft output panel',
    ],
    coming: [
      'Approved action execution engine',
      'Plan versioning and rollback',
    ],
  },

  agent: {
    label:   'AI Agent Projects — B6',
    level:   'red',
    summary: 'Agentic project runner — under active development.',
    working: [
      'Agent project card UI',
      'Step-by-step plan display',
    ],
    coming: [
      'Live agent execution with checkpoint pauses',
      'Multi-agent orchestration',
    ],
  },

  cost: {
    label:   'Cost Templates — B8',
    level:   'green',
    summary: 'Company cost templates and project estimating — operational.',
    working: [
      'Cost template CRUD',
      'Per-project cost estimate linking',
      'Material + labour rate tables',
    ],
    coming: [
      'Supplier quote import',
      'Budget vs actuals variance report',
    ],
  },

  backup: {
    label:   'Backup & DR — B9',
    level:   'green',
    summary: 'Encrypted local backups with restore — operational.',
    working: [
      'Scheduled encrypted SQLite snapshots',
      'Manual backup trigger',
      'Point-in-time restore',
    ],
    coming: [
      'Off-site encrypted cloud backup',
      'Backup health monitoring alerts',
    ],
  },

  health: {
    label:   'Health Monitoring',
    level:   'green',
    summary: 'System health probes and FRIS panel — fully operational.',
    working: [
      'Real-time FRIS status for all engines',
      'Dependency gate inspection',
      'One-click engine recompute',
      'Green / Yellow / Red visual indicators',
    ],
    coming: [
      'Email / Slack alert on status change',
      'Historical uptime charts',
    ],
  },

  settings: {
    label:   'Settings',
    level:   'green',
    summary: 'Workspace, integrations, and admin controls — operational.',
    working: [
      'Theme and display preferences',
      'M365 + Gmail integration toggles',
      'Workspace metadata configuration',
      'User account management',
    ],
    coming: [
      'SSO / LDAP integration (Enterprise)',
      'Audit log viewer',
    ],
  },

  pricing: {
    label:   'License & Subscription',
    level:   'green',
    summary: 'Tiered licensing with PayPal checkout — operational.',
    working: [
      'Trial / Technician / Contractor / Designer tiers',
      'PayPal Checkout order creation',
      'RCDD credential verification gate',
      'Master Admin bypass for admin emails',
    ],
    coming: [
      'Stripe payment fallback',
      'Annual billing discount',
    ],
  },

  'auto-report': {
    label:   'Auto-Report Generator',
    level:   'green',
    summary: 'Project health & activity PDF reports — operational.',
    working: [
      'One-click PDF report generation',
      'Activity timeline per project',
      'RCDD branding and signature block',
      'Print-to-PDF via browser',
    ],
    coming: [
      'Scheduled auto-delivery via email',
      'Custom cover page builder',
    ],
  },

  'global-command': {
    label:   'Global Command Centre',
    level:   'green',
    summary: 'Master Admin world map and license management — operational.',
    working: [
      'Interactive 3D rotating globe with user nodes',
      'Tier distribution analytics',
      'License management table (search, filter)',
      'Extend trial and grant tier actions',
    ],
    coming: [
      'Real-time WebSocket user presence',
      'Deployment version tracking per node',
    ],
  },

  'mail-intelligence': {
    label:   'Mail Intelligence',
    level:   'yellow',
    summary: 'M365 inbox scanning for BIM attachments — beta.',
    working: [
      'Microsoft 365 OAuth PKCE connect flow',
      'BIM file extension filter (DWG, IFC, RVT, PDF…)',
      'Auto-suggest project link via keyword match',
      'Confirm / Dismiss attachment workflow',
    ],
    coming: [
      'Auto-ingest confirmed attachments to Document Register',
      'Delta sync for incremental inbox scanning',
      'Gmail OAuth provider (Phase 2)',
    ],
    notes: 'Requires Microsoft 365 tenant with Mail.Read permission.',
  },

  // Fallback for IDs not in this map
  _default: {
    label:   'Module',
    level:   'green',
    summary: 'Operational.',
    working: ['Core functionality active.'],
    coming:  ['Enhancements in progress.'],
  },
};

export function getFrisContent(featureId: string): FrisContent {
  return FRIS_CONTENT[featureId] ?? (FRIS_CONTENT['_default'] as FrisContent);
}
